import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, toYMD } from "@/lib/apiClient";
import Activate from "@/pages/Activate";
import { QRPanel } from "@/components/QRPanel";
import { EventList } from "@/components/EventList";
import { EventDialog } from "@/components/EventDialog";
import { ScheduleManager } from "@/components/ScheduleManager";
import { AnalyticsPanel } from "@/components/AnalyticsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  QrCode, LayoutGrid, CalendarClock, BarChart3, CreditCard, User, LogOut,
  Bell, Plus, ScanLine, Zap, CheckCircle2, Phone, UserPlus, Menu, ChevronRight,
  ChevronDown, Trash2, Pencil, Layers,
} from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "qr", label: "My QR", icon: QrCode },
  { id: "events", label: "Events", icon: Zap },
  { id: "scheduled", label: "Scheduled", icon: CalendarClock },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "profile", label: "Profile", icon: User },
];

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" }) : "—");

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-border bg-card p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className="mt-2 font-display text-3xl font-extrabold tracking-tighter">{value}</div>
  </div>
);

export default function UserApp() {
  const { user, state, logout, refresh: refreshAuth } = useAuth();
  const [tab, setTab] = useState("overview");
  const [navOpen, setNavOpen] = useState(false);
  const [overview, setOverview] = useState(null);
  const [qr, setQr] = useState(null);
  const [qrs, setQrs] = useState([]);
  const [qrMeta, setQrMeta] = useState({ limit: 1, plan: "Solo" });
  const [currentQrId, setCurrentQrId] = useState(null);
  const [events, setEvents] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [anMode, setAnMode] = useState("current");
  const [aggregate, setAggregate] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");

  const active = state === "ACTIVE" || state === "EXPIRING_SOON";

  const loadNotifs = useCallback(() => api.notifications().then(setNotifs).catch(() => {}), []);
  const loadSub = useCallback(() => api.subscription().then(setSubscription).catch(() => {}), []);

  const refresh = useCallback(async () => {
    if (!active) { loadSub(); loadNotifs(); return; }
    const qrList = await api.listQrs();
    setQrs(qrList.qrs);
    setQrMeta({ limit: qrList.qr_limit, plan: qrList.plan_name });
    let cid = currentQrId;
    if (!cid || !qrList.qrs.find((q) => q.id === cid)) {
      cid = qrList.qrs[0]?.id || null;
      setCurrentQrId(cid);
    }
    const ar = range.from && range.to ? { start: toYMD(range.from), end: toYMD(range.to) } : undefined;
    const [ov, q, ev, sc, an, sub, nf] = await Promise.all([
      api.overview(cid), api.getQr(cid), api.listEvents(cid), api.listSchedules(cid),
      api.analytics(ar, cid), api.subscription(), api.notifications(),
    ]);
    setOverview(ov); setQr(q); setEvents(ev); setSchedules(sc);
    setAnalytics(an); setSubscription(sub); setNotifs(nf);
  }, [active, range, currentQrId, loadSub, loadNotifs]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!active || anMode !== "all") return;
    const ar = range.from && range.to ? { start: toYMD(range.from), end: toYMD(range.to) } : undefined;
    api.analyticsAggregate(ar).then(setAggregate).catch(() => {});
  }, [active, anMode, range]);

  if (state === "DISABLED") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md text-center rounded-2xl border border-border bg-card p-8">
          <h1 className="font-display text-2xl font-extrabold tracking-tighter">Account disabled</h1>
          <p className="mt-2 text-sm text-muted-foreground">Your account has been disabled. Please contact support.</p>
          <Button onClick={logout} variant="outline" className="mt-6 rounded-full" data-testid="disabled-logout"><LogOut className="h-4 w-4 mr-2" />Log out</Button>
        </div>
      </div>
    );
  }

  const activeEvent = events.find((e) => e.id === qr?.active_event_id) || null;

  const handleSaveEvent = async (data) => {
    try {
      if (editing) { await api.updateEvent(editing.id, data, currentQrId); toast.success("Event updated"); }
      else { await api.createEvent(data, currentQrId); toast.success("Event created"); }
      setDialogOpen(false); setEditing(null); await refresh();
    } catch { toast.error("Something went wrong"); }
  };
  const handleActivate = async (id) => { await api.activateEvent(id, currentQrId); toast.success("This event is now Live"); await refresh(); };
  const handleDelete = async (id) => { await api.deleteEvent(id, currentQrId); toast.success("Event deleted"); await refresh(); };
  const handleCreateSchedule = async (d) => { await api.createSchedule(d, currentQrId); await refresh(); };
  const handleDeleteSchedule = async (id) => { await api.deleteSchedule(id, currentQrId); toast.success("Schedule removed"); await refresh(); };
  const handleClearCompleted = async () => { await api.clearCompletedSchedules(currentQrId); toast.success("Cleared"); await refresh(); };
  const handleClearScans = async (days) => { const r = await api.clearScans(days, currentQrId); toast.success(`Deleted ${r.deleted} scans`); await refresh(); };
  const savePhone = async () => { const u = await api.updateQr({ phone: phoneInput.trim() }, currentQrId); setQr(u); setPhoneOpen(false); toast.success("WhatsApp number saved"); };
  const toggleCapture = async (v) => { const u = await api.updateQr({ capture_lead: v }, currentQrId); setQr(u); toast.success(v ? "Visitors will be asked for contact" : "Direct-to-WhatsApp"); };
  const openNotifs = async () => { if (notifs.unread) { await api.markRead(); loadNotifs(); } };

  const atLimit = qrs.length >= qrMeta.limit;
  const PLAN_ORDER = ["solo", "business", "pro"];
  const currentPlanId = subscription?.plan?.id || "solo";
  const recommendedUpgradeId = PLAN_ORDER[Math.min(PLAN_ORDER.indexOf(currentPlanId) + 1, PLAN_ORDER.length - 1)];
  const canUpgrade = recommendedUpgradeId !== currentPlanId;
  const addQr = async () => {
    if (atLimit) {
      if (canUpgrade) setUpgradeOpen(true);
      else toast.error(`You're on the top plan (${qrMeta.plan}) with ${qrMeta.limit} QRs.`);
      return;
    }
    const name = window.prompt("Name this QR (e.g. Product A, Branch – Anna Nagar, Sales – Ravi)", `QR ${qrs.length + 1}`);
    if (name === null) return;
    try {
      const created = await api.createQr({ name: name.trim() });
      toast.success("QR created");
      setCurrentQrId(created.id);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create QR"); }
  };
  const renameQr = async (id) => {
    const cur = qrs.find((q) => q.id === id);
    const name = window.prompt("Rename QR", cur?.name || "");
    if (!name || !name.trim()) return;
    await api.updateQr({ name: name.trim() }, id);
    toast.success("QR renamed");
    await refresh();
  };
  const removeQr = async (id) => {
    if (qrs.length <= 1) { toast.error("You must keep at least one QR."); return; }
    if (!window.confirm("Delete this QR and all its events, schedules and scans? This cannot be undone.")) return;
    try {
      await api.deleteQr(id);
      toast.success("QR deleted");
      if (currentQrId === id) setCurrentQrId(null);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const switchQr = (id) => { if (id !== currentQrId) setCurrentQrId(id); };
  const currentQr = qrs.find((q) => q.id === currentQrId);

  const daysRemaining = subscription?.days_remaining;
  const expiringSoon = state === "EXPIRING_SOON";

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Button variant="ghost" size="icon" className="lg:hidden rounded-full" onClick={() => setNavOpen(!navOpen)} data-testid="mobile-nav-toggle"><Menu className="h-5 w-5" /></Button>
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-accent text-accent-foreground"><QrCode className="h-4 w-4" /></div>
            <span className="font-display text-lg font-extrabold tracking-tighter">DynaQR</span>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu onOpenChange={(o) => o && openNotifs()}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-full relative" data-testid="notifications-btn">
                  <Bell className="h-4 w-4" />
                  {notifs.unread > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-accent text-accent-foreground text-[10px] grid place-items-center">{notifs.unread}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto p-2">
                <p className="px-2 py-1.5 text-xs uppercase tracking-widest text-muted-foreground">Notifications</p>
                {notifs.items.length ? notifs.items.map((n) => (
                  <div key={n.id} className="px-2 py-2 rounded-lg hover:bg-secondary text-sm" data-testid={`notif-${n.id}`}>
                    {n.message}
                    <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                )) : <p className="px-2 py-3 text-sm text-muted-foreground">No notifications yet.</p>}
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="hidden sm:block text-sm font-medium mr-1">{user.full_name}</span>
            <Button variant="outline" size="icon" className="rounded-full" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      {!active ? (
        <Activate isRenewal={state === "EXPIRED"} />
      ) : (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className={`lg:col-span-3 xl:col-span-2 ${navOpen ? "block" : "hidden"} lg:block`}>
            <nav className="lg:sticky lg:top-20 space-y-1">
              {NAV.map((n) => (
                <button key={n.id} data-testid={`nav-${n.id}`}
                  onClick={() => { setTab(n.id); setNavOpen(false); }}
                  className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${tab === n.id ? "bg-accent text-accent-foreground" : "hover:bg-secondary text-foreground"}`}>
                  <n.icon className="h-4 w-4" /> {n.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <main className="lg:col-span-9 xl:col-span-10 space-y-6">
            {["overview", "qr", "events", "scheduled", "analytics"].includes(tab) && qrs.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-3" data-testid="qr-selector-bar">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="hidden sm:block text-xs uppercase tracking-widest text-muted-foreground shrink-0">Active QR</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="rounded-full gap-2 max-w-[240px]" data-testid="qr-switcher-btn">
                        <Layers className="h-4 w-4 shrink-0 text-accent" />
                        <span className="truncate">{currentQr?.name || "My QR"}</span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64 p-1">
                      {qrs.map((q) => (
                        <button key={q.id} data-testid={`qr-option-${q.id}`} onClick={() => switchQr(q.id)}
                          className={`w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-sm hover:bg-secondary ${q.id === currentQrId ? "bg-secondary font-medium" : ""}`}>
                          <span className="truncate flex items-center gap-2"><QrCode className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{q.name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">{q.scans}</span>
                        </button>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Badge variant="outline" className="rounded-full shrink-0" data-testid="qr-usage-badge">{qrs.length}/{qrMeta.limit}</Badge>
                </div>
                <Button size="sm" className="rounded-full" onClick={addQr} data-testid="add-qr-btn"><Plus className="h-4 w-4 mr-1.5" />Add QR</Button>
              </div>
            )}
            {expiringSoon && daysRemaining != null && (
              <div className="rounded-xl border border-accent/40 bg-accent/5 p-4 flex items-center justify-between gap-3" data-testid="expiring-banner">
                <p className="text-sm">Your subscription expires in <strong>{daysRemaining} days</strong>. Renew now to keep your QR active.</p>
                <Button size="sm" className="rounded-full shrink-0" onClick={() => setTab("billing")}>Renew</Button>
              </div>
            )}

            {tab === "overview" && overview && (
              <div className="space-y-6" data-testid="overview-section">
                <h1 className="font-display text-2xl font-extrabold tracking-tighter">Welcome, {user.full_name.split(" ")[0]} 👋</h1>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard label="Total scans" value={overview.total_scans} icon={ScanLine} testid="ov-total" />
                  <StatCard label="Last 7 days" value={overview.last_7_days} icon={BarChart3} testid="ov-7" />
                  <StatCard label="Last 30 days" value={overview.last_30_days} icon={BarChart3} testid="ov-30" />
                  <StatCard label="Total events" value={overview.total_events} icon={Zap} testid="ov-events" />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="rounded-xl border border-border bg-card p-5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Active event</p>
                    {overview.active_event ? (
                      <>
                        <p className="font-display text-lg font-bold tracking-tight">{overview.active_event.name}</p>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{overview.active_event.message}</p>
                      </>
                    ) : <p className="text-sm text-muted-foreground">No active event yet.</p>}
                    <div className="mt-3"><Badge className="bg-accent text-accent-foreground">QR {overview.qr_status}</Badge></div>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Current subscription</p>
                    <p className="font-display text-lg font-bold tracking-tight">{(subscription?.plan?.name || "Solo").toUpperCase()} PLAN · ₹{subscription?.plan?.price ?? 499}/year</p>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="h-2 w-2 rounded-full bg-accent" /> Active · expires {fmtDay(subscription?.subscription?.expiry_date)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{daysRemaining} days remaining · {subscription?.qr_count ?? qrs.length}/{subscription?.qr_limit ?? qrMeta.limit} QRs used</p>
                    <Button size="sm" variant="outline" className="mt-3 rounded-full" onClick={() => setTab("billing")}>Manage subscription</Button>
                  </div>
                </div>
              </div>
            )}

            {tab === "qr" && qr && (
              <div className="space-y-6" data-testid="qr-section">
                <div className="grid md:grid-cols-2 gap-6">
                  <QRPanel activeEvent={activeEvent} phone={qr.phone} slug={qr.slug} />
                  <div className="space-y-4">
                  <div className="rounded-2xl border border-border bg-card p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-display text-lg font-bold tracking-tight">QR settings</h3>
                      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => renameQr(currentQrId)} data-testid="rename-current-qr-btn"><Pencil className="h-3.5 w-3.5 mr-1.5" />Rename</Button>
                    </div>
                    <p className="text-sm text-muted-foreground -mt-2 mb-4">Editing: <span className="font-medium text-foreground">{currentQr?.name || "My QR"}</span></p>
                    <div className="flex items-center justify-between rounded-xl border border-border p-4">
                      <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><span className="text-sm">WhatsApp number</span></div>
                      <Dialog open={phoneOpen} onOpenChange={(o) => { setPhoneOpen(o); if (o) setPhoneInput(qr.phone || ""); }}>
                        <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full" data-testid="qr-phone-btn">{qr.phone || "Set"}</Button></DialogTrigger>
                        <DialogContent className="rounded-2xl">
                          <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tighter">WhatsApp number</DialogTitle></DialogHeader>
                          <div className="space-y-2 py-2">
                            <Label>Number with country code</Label>
                            <Input data-testid="qr-phone-input" placeholder="e.g. 919876543210" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} />
                          </div>
                          <DialogFooter><Button className="rounded-full" onClick={savePhone} data-testid="qr-phone-save">Save</Button></DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="flex items-center justify-between rounded-xl border border-border p-4 mt-3">
                      <div className="flex items-center gap-2"><UserPlus className="h-4 w-4 text-muted-foreground" /><span className="text-sm">Ask visitors for contact</span></div>
                      <Switch checked={!!qr.capture_lead} onCheckedChange={toggleCapture} data-testid="capture-lead-switch" />
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">When on, visitors enter their name & number (skippable) before WhatsApp opens — captured in your scan reports.</p>
                  </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-6" data-testid="qr-manage-list">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-lg font-bold tracking-tight">Your QR codes <span className="text-muted-foreground font-medium">({qrs.length}/{qrMeta.limit})</span></h3>
                    <Button size="sm" className="rounded-full" onClick={addQr} data-testid="manage-add-qr-btn"><Plus className="h-4 w-4 mr-1.5" />Add QR</Button>
                  </div>
                  <div className="space-y-2">
                    {qrs.map((q) => (
                      <div key={q.id} data-testid={`qr-row-${q.id}`}
                        className={`flex items-center gap-3 rounded-xl border p-3 ${q.id === currentQrId ? "border-accent bg-accent/5" : "border-border"}`}>
                        <span className="grid h-9 w-9 place-items-center rounded-lg bg-secondary shrink-0"><QrCode className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{q.name}{q.id === currentQrId && <span className="ml-2 text-xs text-accent">• editing</span>}</p>
                          <p className="text-xs text-muted-foreground truncate">{q.active_event_name ? `Live: ${q.active_event_name}` : "No live event"} · {q.events} events · {q.scans} scans</p>
                        </div>
                        {q.id !== currentQrId && <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => switchQr(q.id)} data-testid={`qr-open-${q.id}`}>Open</Button>}
                        <Button size="icon" variant="ghost" className="rounded-full shrink-0 h-8 w-8" onClick={() => renameQr(q.id)} data-testid={`qr-rename-${q.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        {qrs.length > 1 && <Button size="icon" variant="ghost" className="rounded-full shrink-0 h-8 w-8 text-destructive hover:text-destructive" onClick={() => removeQr(q.id)} data-testid={`qr-delete-${q.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    ))}
                  </div>
                  {atLimit && canUpgrade && <p className="mt-3 text-xs text-muted-foreground">You've reached your {qrMeta.plan} plan limit. <button className="text-accent hover:underline" onClick={() => setUpgradeOpen(true)} data-testid="upgrade-link">Upgrade</button> to add more QRs.</p>}
                </div>
              </div>
            )}

            {tab === "events" && (
              <div data-testid="events-section">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tighter">Events</h1>
                    <p className="text-sm text-muted-foreground">for <span className="font-medium text-foreground">{currentQr?.name || "My QR"}</span></p>
                  </div>
                  <Button className="rounded-full" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="event-create-btn"><Plus className="h-4 w-4 mr-1.5" />New event</Button>
                </div>
                <EventList events={events} activeId={qr?.active_event_id} onActivate={handleActivate}
                  onEdit={(ev) => { setEditing(ev); setDialogOpen(true); }} onDelete={handleDelete} />
              </div>
            )}

            {tab === "scheduled" && (
              <ScheduleManager events={events} schedules={schedules} onCreate={handleCreateSchedule}
                onDelete={handleDeleteSchedule} onClearCompleted={handleClearCompleted} />
            )}

            {tab === "analytics" && (
              <div data-testid="analytics-section">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tighter">Analytics</h1>
                    <p className="text-sm text-muted-foreground">{anMode === "all" ? "across all your QRs" : <>for <span className="font-medium text-foreground">{currentQr?.name || "My QR"}</span></>}</p>
                  </div>
                  {qrs.length > 1 && (
                    <div className="inline-flex rounded-full border border-border p-1" data-testid="analytics-mode-toggle">
                      <button onClick={() => setAnMode("current")} data-testid="analytics-mode-current"
                        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${anMode === "current" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}>This QR</button>
                      <button onClick={() => setAnMode("all")} data-testid="analytics-mode-all"
                        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${anMode === "all" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}>All QRs</button>
                    </div>
                  )}
                </div>
                {anMode === "all"
                  ? <AggregateAnalytics data={aggregate} range={range} onRangeChange={setRange} onSelectQr={(id) => { setCurrentQrId(id); setAnMode("current"); }} />
                  : <AnalyticsPanel data={analytics} onClearScans={handleClearScans} range={range} onRangeChange={setRange} qrId={currentQrId} />}
              </div>
            )}

            {tab === "billing" && subscription && (
              <div className="space-y-6" data-testid="billing-section">
                <h1 className="font-display text-2xl font-extrabold tracking-tighter">Billing & Subscription</h1>
                <div className="rounded-2xl border border-border bg-card p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-xl font-bold">{(subscription.plan?.name || "Solo").toUpperCase()} · ₹{subscription.plan?.price}/year</p>
                      <div className="mt-2 flex items-center gap-2 text-sm"><span className="h-2 w-2 rounded-full bg-accent" /> {state === "EXPIRING_SOON" ? "Active (expiring soon)" : "Active"}</div>
                      <p className="text-sm text-muted-foreground mt-1">Started {fmtDay(subscription.subscription?.start_date)} · Expires {fmtDay(subscription.subscription?.expiry_date)}</p>
                      <p className="text-sm text-muted-foreground">{daysRemaining} days remaining · {subscription.qr_count ?? qrs.length}/{subscription.qr_limit ?? qrMeta.limit} QRs used</p>
                    </div>
                    <div className="w-full sm:w-auto"><Renew plan={subscription.plan} /></div>
                  </div>
                </div>
                <PaymentHistory />
              </div>
            )}

            {tab === "profile" && <Profile />}
          </main>
        </div>
      )}

      <EventDialog open={dialogOpen} onOpenChange={(o) => (o ? setDialogOpen(true) : (setDialogOpen(false), setEditing(null)))} onSave={handleSaveEvent} event={editing} />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} planName={qrMeta.plan} limit={qrMeta.limit} recommendedId={recommendedUpgradeId} onDone={refresh} />
    </div>
  );
}

function Renew({ plan }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full w-full" data-testid="renew-btn">Renew / Upgrade — ₹{plan?.price ?? 499}</Button></DialogTrigger>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tighter">Renew or upgrade</DialogTitle>
        <DialogDescription>Pick a plan and pay via UPI. Your subscription activates after a quick verification.</DialogDescription></DialogHeader>
        <Activate isRenewal defaultPlanId={plan?.id} />
      </DialogContent>
    </Dialog>
  );
}

function UpgradeDialog({ open, onOpenChange, planName, limit, recommendedId, onDone }) {
  const label = recommendedId ? recommendedId.charAt(0).toUpperCase() + recommendedId.slice(1) : "a higher";
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onDone?.(); }}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tighter">Upgrade to {label} to add more QRs</DialogTitle>
          <DialogDescription>
            You've used all {limit} QR{limit > 1 ? "s" : ""} on your {planName} plan. Upgrade to unlock more dynamic QRs — everything you've set up stays exactly as it is.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-accent/40 bg-accent/5 p-3 text-sm text-muted-foreground flex items-center gap-2">
          <Layers className="h-4 w-4 text-accent shrink-0" />
          Pick your new plan below and pay via UPI. Your extra QRs unlock as soon as we verify the payment.
        </div>
        <Activate isRenewal defaultPlanId={recommendedId} />
      </DialogContent>
    </Dialog>
  );
}

function AggregateAnalytics({ data, onSelectQr }) {
  if (!data) return <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="aggregate-loading">Loading combined analytics…</div>;
  const max = Math.max(1, ...(data.per_qr || []).map((q) => q.scans));
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString([], { day: "numeric", month: "short" }) : "—");
  return (
    <div className="space-y-6" data-testid="aggregate-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Scans (range)" value={data.total_scans} icon={ScanLine} testid="agg-total" />
        <StatCard label="Last 7 days" value={data.last_7_days} icon={BarChart3} testid="agg-7" />
        <StatCard label="Last 30 days" value={data.last_30_days} icon={BarChart3} testid="agg-30" />
        <StatCard label="Your QRs" value={data.qr_count} icon={Layers} testid="agg-qrs" />
      </div>
      <div className="rounded-2xl border border-border bg-card p-6">
        <p className="font-display font-bold mb-4">Scans by QR</p>
        {data.per_qr?.length ? (
          <div className="space-y-3">
            {data.per_qr.map((q) => (
              <div key={q.qr_id} data-testid={`agg-qr-${q.qr_id}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <button className="font-medium hover:text-accent flex items-center gap-2 min-w-0" onClick={() => onSelectQr?.(q.qr_id)} data-testid={`agg-open-${q.qr_id}`}>
                    <QrCode className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{q.name}</span>
                  </button>
                  <span className="text-muted-foreground shrink-0 ml-2">{q.scans} <span className="text-xs">({q.percent}%)</span></span>
                </div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full bg-accent" style={{ width: `${(q.scans / max) * 100}%` }} />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Today: {q.today} · All-time: {q.total_scans} · Last scan: {fmt(q.last_scanned)}</p>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">No scans yet across your QRs.</p>}
      </div>
    </div>
  );
}


function PaymentHistory() {
  const [payments, setPayments] = useState([]);
  useEffect(() => { api.myPayments().then(setPayments); }, []);
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="billing-history">
      <p className="font-display font-bold mb-3">Payment history</p>
      {payments.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="pb-2">Date</th><th className="pb-2">Amount</th><th className="pb-2">UTR</th><th className="pb-2">Status</th></tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="py-2">{p.payment_date}</td><td className="py-2">₹{p.amount}</td><td className="py-2">{p.utr}</td>
                  <td className="py-2"><Badge className={p.status === "approved" ? "bg-accent text-accent-foreground" : p.status === "rejected" ? "bg-destructive text-destructive-foreground" : "bg-secondary text-secondary-foreground"}>{p.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-muted-foreground">No payments yet.</p>}
    </div>
  );
}

function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ full_name: user.full_name, business_name: user.business_name || "", mobile: user.mobile || "", organisation: user.organisation || "", chapter: user.chapter || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => { await api.updateProfile(form); await refresh(); toast.success("Profile updated"); };
  const changePw = async () => {
    try { await api.changePassword(pw); setPw({ current_password: "", new_password: "" }); toast.success("Password changed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-section">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">My Profile</h1>
      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Name</Label><Input data-testid="profile-name" value={form.full_name} onChange={set("full_name")} /></div>
          <div className="space-y-2"><Label>Business name</Label><Input value={form.business_name} onChange={set("business_name")} /></div>
          <div className="space-y-2"><Label>Email</Label><Input value={user.email} disabled /></div>
          <div className="space-y-2"><Label>Mobile</Label><Input value={form.mobile} onChange={set("mobile")} /></div>
          <div className="space-y-2"><Label>Organisation</Label><Input value={form.organisation} onChange={set("organisation")} /></div>
          <div className="space-y-2"><Label>Chapter</Label><Input value={form.chapter} onChange={set("chapter")} /></div>
        </div>
        <Button className="rounded-full" onClick={save} data-testid="profile-save">Save changes</Button>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <p className="font-display font-bold">Change password</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
          <div className="space-y-2"><Label>New password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
        </div>
        <Button variant="outline" className="rounded-full" onClick={changePw} data-testid="change-pw-btn">Update password</Button>
      </div>
    </div>
  );
}
